import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { validateField } from '../utils/wizardValidation';
import { debounce } from '../utils/fileUtils.js';

/**
 * Enhanced form input with real-time validation and error handling
 */
const EnhancedFormInput = ({
  label,
  name,
  type = 'text',
  value = '',
  onChange,
  onBlur,
  placeholder = '',
  required = false,
  disabled = false,
  error = null,
  hasError = false,
  helpText = '',
  validationRules = null,
  showValidationIcon = true,
  autoComplete = 'off',
  min,
  max,
  step,
  className = '',
  ...props
}) => {
  const { theme } = useTheme();
  const [localError, setLocalError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [validationState, setValidationState] = useState('idle'); // idle, validating, valid, invalid

  // Debounced validation function
  const debouncedValidate = useCallback(
    debounce(async (fieldName, fieldValue, rules) => {
      if (!rules || !hasBeenTouched) return;
      
      setIsValidating(true);
      setValidationState('validating');
      
      try {
        const validationError = validateField(fieldName, fieldValue, rules);
        setLocalError(validationError);
        setValidationState(validationError ? 'invalid' : 'valid');
      } catch (error) {
        console.warn('Validation error:', error);
        setLocalError(null);
        setValidationState('idle');
      } finally {
        setIsValidating(false);
      }
    }, 300),
    [hasBeenTouched]
  );

  // Run validation when value changes
  useEffect(() => {
    if (validationRules && hasBeenTouched) {
      debouncedValidate(name, value, validationRules);
    }
  }, [value, validationRules, name, debouncedValidate, hasBeenTouched]);

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Clear errors immediately when user starts typing
    if (localError || error) {
      setLocalError(null);
      setValidationState('idle');
    }
    
    if (onChange) {
      onChange(e);
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    setHasBeenTouched(true);
    
    if (onBlur) {
      onBlur(e);
    }
    
    // Trigger immediate validation on blur
    if (validationRules) {
      const validationError = validateField(name, value, validationRules);
      setLocalError(validationError);
      setValidationState(validationError ? 'invalid' : 'valid');
    }
  };

  // Determine which error to show (prop error takes precedence)
  const displayError = error || localError;
  const showError = hasError || (hasBeenTouched && !!displayError);

  // Determine validation icon
  const getValidationIcon = () => {
    if (!showValidationIcon || !hasBeenTouched || disabled) return null;
    
    if (isValidating) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <div className={`animate-spin h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full`}></div>
        </div>
      );
    }
    
    if (validationState === 'valid' && !displayError) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    
    if (validationState === 'invalid' || showError) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    
    return null;
  };

  // Input styling based on validation state
  const getInputStyling = () => {
    const baseClasses = `block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 sm:text-sm transition-colors duration-200 ${showValidationIcon ? 'pr-10' : ''}`;
    
    if (disabled) {
      return `${baseClasses} ${
        theme === 'dark'
          ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
          : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
      }`;
    }
    
    if (showError) {
      return `${baseClasses} ${
        theme === 'dark'
          ? 'bg-gray-800 border-red-500 text-white focus:ring-red-500 focus:border-red-500'
          : 'bg-white border-red-300 text-gray-900 focus:ring-red-500 focus:border-red-500'
      }`;
    }
    
    if (validationState === 'valid' && hasBeenTouched) {
      return `${baseClasses} ${
        theme === 'dark'
          ? 'bg-gray-800 border-green-500 text-white focus:ring-green-500 focus:border-green-500'
          : 'bg-white border-green-300 text-gray-900 focus:ring-green-500 focus:border-green-500'
      }`;
    }
    
    return `${baseClasses} ${
      theme === 'dark'
        ? 'bg-gray-800 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
        : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
    }`;
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={name}
          className={`block text-sm font-medium ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input Container */}
      <div className="relative">
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          min={min}
          max={max}
          step={step}
          className={getInputStyling()}
          aria-invalid={showError}
          aria-describedby={
            showError ? `${name}-error` : 
            helpText ? `${name}-help` : undefined
          }
          {...props}
        />
        
        {/* Validation Icon */}
        {getValidationIcon()}
      </div>
      
      {/* Error Message */}
      {showError && displayError && (
        <div 
          id={`${name}-error`}
          className="flex items-center text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{displayError}</span>
        </div>
      )}
      
      {/* Help Text */}
      {helpText && !showError && (
        <div 
          id={`${name}-help`}
          className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {helpText}
        </div>
      )}
      
      {/* Validation State Indicator for Screen Readers */}
      {hasBeenTouched && (
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {isValidating && 'Validating input...'}
          {validationState === 'valid' && !displayError && 'Input is valid'}
          {validationState === 'invalid' && displayError && `Error: ${displayError}`}
        </div>
      )}
    </div>
  );
};

export default EnhancedFormInput;
